import { useEffect, useState } from "react";
import { Outlet, useLocation } from "react-router-dom";

import { useNavigateWithSameKey } from "../../../../../custom-hooks/navigateWithKey";

import { useDispatch } from "react-redux";
import { createAutomationEntry } from "../../../../../store/slices/test-automation/testAutomationSlice";

import { Box, Typography, Paper, Grid2, Button, Divider, Dialog, DialogTitle, DialogContent, DialogActions } from "@mui/material";

import NavigationRail from "../../../../common-components/NavigationRail";

// Import custom SVG icons for test automation
import { ReactComponent as WebIcon } from "../../../../../images/test-automation/landingPage_webIcon.svg";
import { ReactComponent as E2EIcon } from "../../../../../images/test-automation/landingPage_e2eIcon.svg";
import { ReactComponent as APIIcon } from "../../../../../images/test-automation/landingPage_apiIcon.svg";
import { ReactComponent as LocatorsIcon } from "../../../../../images/test-automation/landingPage_locatorEngineIcon.svg";
import { ReactComponent as DesktopIcon } from "../../../../../images/test-automation/landingPage_desktopIcon.svg";
import { ReactComponent as MobileIcon } from "../../../../../images/test-automation/landingPage_mobileIcon.svg";
import { ReactComponent as BulbIcon } from "../../../../../images/test-automation/landingPage_bulbIcon.svg";
import { ReactComponent as HelpIconSVG } from "../../../../../images/test-automation/landingPage_helpIcon.svg";
import { setWebAutomation } from "../../../../../store/slices/test-automation/web/webAutomationSlice";

const TestAutomationLandingPage = () => {
  const navigate = useNavigateWithSameKey();
  const location = useLocation();
  const dispatch = useDispatch();

  const locationKey = location.state?.locationKey;

  const [frameworkPopupOpen, setFrameworkPopupOpen] = useState(false);

  useEffect(() => {
    if (!locationKey) {
      navigate("/landingpage");
      return;
    }

    dispatch(createAutomationEntry({ locationKey }));
  }, [locationKey]);


  const automationTypes = [
    {
      id: "web",
      title: "Web",
      description:
        "Cross-browser automation for Chrome, Firefox, Safari, and Edge with parallel execution and responsive testing.",
      icon: <WebIcon style={{ width: 24, height: 24 }} />,
      path: "/dashboard/test-automation/configure-preferences",
      clickable: true,
    },
    {
      id: "e2e",
      title: "E2E (End-to-End)",
      description:
        "End-to-end user journey validation across systems with workflow automation and data verification.",
      icon: <E2EIcon style={{ width: 24, height: 24 }} />,
      path: "/dashboard/test-automation/configure-preferences",
      clickable: false,
    },
    {
      id: "api",
      title: "API",
      description:
        "REST and GraphQL testing with request validation, schema verification, and performance benchmarking.",
      icon: <APIIcon style={{ width: 24, height: 24 }} />,
      path: "/dashboard/test-automation/configure-preferences",
      clickable: false,
    },
    {
      id: "locators",
      title: "Locators Engine",
      description:
        "Smart element locator generation from DOM. Paste HTML and get CSS selectors, XPath, and data attributes.",
      icon: <LocatorsIcon style={{ width: 24, height: 24 }} />,
      path: "/dashboard/cag-locators/cag-locators-generation",
      clickable: true,
    },
    {
      id: "desktop",
      title: "Desktop",
      description:
        "Windows desktop automation for WPF, WinForms, Win32, and UWP applications with UI interaction.",
      icon: <DesktopIcon style={{ width: 24, height: 24 }} />,
      path: "/dashboard/test-automation/configure-preferences",
      clickable: false,
    },
    {
      id: "mobile",
      title: "Mobile",
      description:
        "Native and hybrid iOS/Android testing with real device support, gestures, and network simulation.",
      icon: <MobileIcon style={{ width: 24, height: 24 }} />,
      path: "/dashboard/test-automation/configure-preferences",
      clickable: false,
    },
  ];

  const handleCardClick = (automationType) => {
    if (automationType.clickable) {
      if (automationType.id === "web") {
        // Show framework selection popup for Web card
        setFrameworkPopupOpen(true);
      } else {
        // Navigate directly for other clickable cards
        navigate(automationType.path, {
          state: { automationType: automationType.id },
        });
      }
    }
  };

  const handleFrameworkSelection = (framework) => {
    setFrameworkPopupOpen(false);
    if (framework === "playwright") {
      dispatch(setWebAutomation(locationKey));
      navigate("/dashboard/test-automation/configure-preferences", {
        state: { automationType: "web" },
      });
    } else if (framework === "other") {
      navigate("/dashboard/test-automation/SelectFrameWork", {
        state: { automationType: "web" },
      });
    }
  };

  const handleClosePopup = () => {
    setFrameworkPopupOpen(false);
  };

  // Only show landing page content if we're on the main test automation route
  if (location.pathname !== "/dashboard/test-automation") {
    return <Outlet />;
  }

  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "center",
        alignItems: "flex-start",
        minHeight: "100vh",
        bgcolor: "#F6F3F2",
        overflow: "auto",
      }}
    >
      <Grid2
        container
        spacing={1.5}
        sx={{
          minHeight: "100vh",
          display: "flex",
          flexWrap: "nowrap",
          justifyContent: "center",
          alignItems: "flex-start",
          overflow: "visible",
        }}
      >
        {/* NavigationRail Section */}
        <Grid2
          item
          xs={12}
          md={2}
          sx={{
            display: { xs: "none", md: "block" },
            minHeight: "100vh",
          }}
        >
          <NavigationRail />
        </Grid2>

        {/* Main Content */}
        <Box
          sx={{
            minHeight: "100vh",
            background: "#F6F3F2",
            maxWidth: "100%",
            flexGrow: 1,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            px: 2,
            py: 4,
            overflow: "visible",
          }}
        >
          {/* Header */}
          <Box
            sx={{
              width: "768px",
              height: "140px",
              paddingTop: "16px",
              paddingRight: "16px",
              paddingLeft: "16px",
              display: "flex",
              flexDirection: "column",
              gap: "24px",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Typography
              variant="h3"
              component="h1"
              sx={{
                fontFamily: "Manrope",
                fontWeight: 400,
                fontStyle: "normal",
                fontSize: "32px",
                lineHeight: "38px",
                letterSpacing: "0.2px",
                textAlign: "center",
                color: "#000000",
                margin: 0,
              }}
            >
              Test Automation
            </Typography>
            <Typography
              variant="body1"
              sx={{
                fontFamily: "Manrope",
                fontWeight: 400,
                fontStyle: "normal",
                fontSize: "14px",
                lineHeight: "1.15",
                letterSpacing: "0.08px",
                textAlign: "center",
                color: "#525252",
                margin: 0,
              }}
            >
              Choose your automation type and start building projects
              effortlessly
            </Typography>
          </Box>
          <Divider
            sx={{
              width: "100%",
              maxWidth: "836px",
              borderBottom: "1px solid #BFC7D5",
              mb: 3,
            }}
          />

          {/* Cards Layout */}
          <Box
            sx={{
              width: "678px",
              height: "480px",
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: "24px",
              mb: 4,
            }}
          >
            {automationTypes.map((automationType, index) => (
              <Paper
                key={index}
                elevation={0}
                sx={{
                  width: "327px",
                  height: "144px",
                  padding: "14px",
                  cursor: automationType.clickable ? "pointer" : "default",
                  transition: "transform 0.2s",
                  background: automationType.clickable ? "#FFFFFF" : "#F5F5F5",
                  opacity: automationType.clickable ? 1 : 0.7,
                  borderRadius: "8px",
                  "&:hover": {
                    transform: automationType.clickable
                      ? "translateY(-2px)"
                      : "none",
                  },
                }}
                onClick={() => handleCardClick(automationType)}
              >
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    textAlign: "center",
                    height: "100%",
                  }}
                >
                  {/* Header: Icon + Title side by side */}
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: "8px",
                      mb: 1.5,
                    }}
                  >
                    {automationType.icon}
                    <Typography
                      variant="h6"
                      sx={{
                        fontFamily: "Manrope",
                        fontWeight: 700,
                        fontSize: "14px",
                        lineHeight: "18px",
                        letterSpacing: "0.1px",
                        color: "#000000",
                      }}
                    >
                      {automationType.title}
                    </Typography>
                  </Box>

                  {/* Description */}
                  <Typography
                    variant="body2"
                    sx={{
                      fontFamily: "Manrope",
                      fontWeight: 400,
                      fontSize: "12px",
                      lineHeight: "16px",
                      letterSpacing: "0.1px",
                      color: "#666666",
                    }}
                  >
                    {automationType.description}
                  </Typography>
                </Box>
              </Paper>
            ))}
          </Box>

          {/* Footer Layout */}
          <Box
            sx={{
              width: "808px",
              height: "114px",
              display: "flex",
              flexDirection: "column",
              gap: "40px",
              alignItems: "center",
              justifyContent: "center",
              mt: 10,
            }}
          >
            {/* Tip Section */}
            <Paper
              elevation={0}
              sx={{
                width: "100%",
                height: "auto",
                minHeight: "42px",
                borderRadius: 0,
                backgroundColor: "#F8F8F8",
                border: "none",
                display: "flex",
                alignItems: "center",
                padding: "10px 12px",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  width: "100%",
                }}
              >
                <Box
                  sx={{
                    width: "32px",
                    height: "32px",
                    borderRadius: "6px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                  }}
                >
                  <BulbIcon style={{ width: "32px", height: "32px" }} />
                </Box>
                <Box
                  sx={{ display: "flex", flexDirection: "column", gap: "1px" }}
                >
                  <Typography
                    variant="subtitle1"
                    sx={{
                      fontFamily: "Manrope",
                      fontWeight: 700,
                      fontSize: "12px",
                      color: "#333333",
                      lineHeight: "15px",
                      fontStyle: "normal",
                    }}
                  >
                    Here is a tip for you
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      fontFamily: "Manrope",
                      fontWeight: 400,
                      fontSize: "10px",
                      color: "#666666",
                      lineHeight: "13px",
                    }}
                  >
                    Most teams start with AI Import for faster setup. You can
                    always switch to manual configuration.
                  </Typography>
                </Box>
              </Box>
            </Paper>

            {/* Need Help Section */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: "6px",
              }}
            >
              <Button
                variant="text"
                sx={{
                  color: "#0094FB",
                  textTransform: "none",
                  fontSize: "12px",
                  fontWeight: 500,
                  textDecoration: "underline",
                  padding: 0,
                  minWidth: "auto",
                  "&:hover": {
                    backgroundColor: "transparent",
                  },
                }}
              >
                Need Help?
              </Button>
              <HelpIconSVG style={{ width: "16px", height: "16px" }} />
            </Box>
          </Box>
        </Box>
      </Grid2>

      {/* Framework Selection Popup */}
      <Dialog
        open={frameworkPopupOpen}
        onClose={handleClosePopup}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: "12px",
            padding: "24px",
          },
        }}
      >
        <DialogTitle
          sx={{
            fontFamily: "Manrope",
            fontWeight: 700,
            fontSize: "20px",
            color: "#000000",
            textAlign: "center",
            paddingBottom: "16px",
          }}
        >
          Select Framework
        </DialogTitle>
        <DialogContent sx={{ padding: "0 24px" }}>
          <Typography
            sx={{
              fontFamily: "Manrope",
              fontWeight: 400,
              fontSize: "14px",
              color: "#666666",
              textAlign: "center",
              marginBottom: "24px",
            }}
          >
            Choose your preferred framework for web automation
          </Typography>
          
          <Box sx={{ display: "flex", flexDirection: "column", gap: "12px" }}>
            <Button
              variant="outlined"
              onClick={() => handleFrameworkSelection("playwright")}
              sx={{
                padding: "16px 24px",
                borderRadius: "8px",
                borderColor: "#0094FB",
                color: "#0094FB",
                fontFamily: "Manrope",
                fontWeight: 600,
                fontSize: "16px",
                textTransform: "none",
                "&:hover": {
                  borderColor: "#0078D4",
                  backgroundColor: "rgba(0, 148, 251, 0.04)",
                },
              }}
            >
              Playwright New Design
            </Button>
            
            <Button
              variant="outlined"
              onClick={() => handleFrameworkSelection("other")}
              sx={{
                padding: "16px 24px",
                borderRadius: "8px",
                borderColor: "#0094FB",
                color: "#0094FB",
                fontFamily: "Manrope",
                fontWeight: 600,
                fontSize: "16px",
                textTransform: "none",
                "&:hover": {
                  borderColor: "#0078D4",
                  backgroundColor: "rgba(0, 148, 251, 0.04)",
                },
              }}
            >
              Other Frameworks
            </Button>
          </Box>
        </DialogContent>
        <DialogActions sx={{ padding: "16px 24px 0" }}>
          <Button
            onClick={handleClosePopup}
            sx={{
              color: "#666666",
              fontFamily: "Manrope",
              fontWeight: 500,
              fontSize: "14px",
              textTransform: "none",
              "&:hover": {
                backgroundColor: "transparent",
              },
            }}
          >
            Cancel
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default TestAutomationLandingPage;
